import { useEffect, useRef, useState } from "react";
import { Bot, Send, X, Loader2 } from "lucide-react";
import api from "../../lib/api";

const TEAL = "#17A2A6";
const NAVY = "#14304F";

/** Public AI concierge — answers only from hotel-approved page content. */
export const ConciergeChat = ({ property }) => {
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);
  const [sid] = useState(() => {
    const key = `hp-concierge-${property.slug}`;
    const v = sessionStorage.getItem(key) || crypto.randomUUID();
    sessionStorage.setItem(key, v);
    return v;
  });

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs, busy]);

  const send = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setMsgs((m) => [...m, { role: "guest", text }]);
    setBusy(true);
    try {
      const { data } = await api.post(`/directstay/concierge/${property.slug}`, { session_id: sid, message: text });
      setMsgs((m) => [...m, { role: "concierge", text: data.answer }]);
    } catch (err) {
      setMsgs((m) => [...m, { role: "concierge", text: err.response?.data?.detail || "I'm unavailable right now — please use the inquiry form on this page and the property will reply directly." }]);
    } finally { setBusy(false); }
  };

  if (!open) return (
    <button data-testid="concierge-open" onClick={() => setOpen(true)}
      className="fixed bottom-5 right-5 z-50 inline-flex items-center gap-2 text-white font-700 text-sm rounded-full px-5 py-3 shadow-xl hover:opacity-90 transition-opacity" style={{ background: TEAL }}>
      <Bot className="w-4 h-4" /> Ask the Property AI
    </button>
  );

  return (
    <div data-testid="concierge-panel" className="fixed bottom-5 right-5 z-50 w-[92vw] max-w-sm bg-white rounded-2xl shadow-2xl border border-zinc-200 flex flex-col overflow-hidden" style={{ maxHeight: "70vh" }}>
      <div className="flex items-center gap-2 px-4 py-3 text-white shrink-0" style={{ background: NAVY }}>
        <span className="w-7 h-7 rounded-full flex items-center justify-center" style={{ background: TEAL }}><Bot className="w-4 h-4" /></span>
        <div className="min-w-0">
          <div className="text-sm font-700 truncate">{property.name}</div>
          <div className="text-[10px] text-stone-300">AI Concierge · hotel-approved answers</div>
        </div>
        <button data-testid="concierge-close" onClick={() => setOpen(false)} className="ml-auto p-1 hover:bg-white/10 rounded"><X className="w-4 h-4" /></button>
      </div>

      <div data-testid="concierge-messages" className="flex-1 overflow-y-auto p-3 space-y-2.5 bg-stone-50" style={{ minHeight: 180 }}>
        <div className="text-xs text-zinc-600 bg-white border border-zinc-200 rounded-xl rounded-tl-sm px-3 py-2 max-w-[85%]">
          Hi! Ask me anything about {property.name} — rooms, amenities, getting here, policies. Rates and availability are always confirmed by the property.
        </div>
        {msgs.map((m, i) => m.role === "guest" ? (
          <div key={i} className="flex justify-end"><div className="text-sm text-white rounded-xl rounded-tr-sm px-3 py-2 max-w-[85%]" style={{ background: TEAL }}>{m.text}</div></div>
        ) : (
          <div key={i} className="text-sm text-zinc-700 bg-white border border-zinc-200 rounded-xl rounded-tl-sm px-3 py-2 max-w-[85%] whitespace-pre-line">{m.text}</div>
        ))}
        {busy && <div className="inline-flex items-center gap-2 text-xs text-zinc-400 bg-white border border-zinc-200 rounded-xl px-3 py-2"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Thinking…</div>}
        <div ref={endRef} />
      </div>

      <form onSubmit={send} className="flex items-center gap-2 border-t border-zinc-200 p-2.5 shrink-0">
        <input data-testid="concierge-input" value={input} onChange={(e) => setInput(e.target.value)} placeholder="Ask about this property…"
          className="flex-1 text-sm border border-zinc-300 rounded-full px-4 py-2 focus:outline-none focus:border-teal-500" />
        <button data-testid="concierge-send" disabled={busy || !input.trim()} className="w-9 h-9 rounded-full text-white flex items-center justify-center disabled:opacity-40" style={{ background: TEAL }}>
          <Send className="w-4 h-4" />
        </button>
      </form>
      <p className="text-[9px] text-zinc-400 text-center pb-2 px-3">Answers come from hotel-approved information only.</p>
    </div>
  );
};
